/**
 * API Response Transformers
 * 
 * These functions transform Odoo API responses into frontend types.
 * Handles translation mapping, field name differences, and data normalization.
 */

import type { Post, Partner, Talent, PaginatedResponse } from '@/types';
import { buildImageUrl } from '@/config/api.config';

/**
 * Odoo API Response Types
 */
interface OdooResponse<T> {
  success: boolean;
  data: T;
  total_records?: number;
  language?: string;
  message?: string;
}

interface OdooNewsItem {
  id: number;
  title: string; // Translated based on lang parameter
  slug: string | false | null;
  date: string | null;
  department: string | null;
  summary: string;
  content: string;
  image: string | null;
  author: string | null;
  tags: string[];
  images?: Array<{
    id: number;
    name: string;
    file_url: string;
    mimetype: string;
    file_size: number;
    create_date: string;
    description: string;
  }>;
  slider_images?: Array<{
    id: number;
    image: string;
    sequence: number;
  }>;
}

interface OdooPartner {
  id: number;
  name: string;
  code: string | false | null; // Can be false
  title: string | false | null; // Can be false
  slug: string | false | null; // Can be false
  partner_type: 'local' | 'international' | 'government' | 'private';
  logo: string | null;
  description: string;
  phone: string | null;
  email: string | null;
  website: string | false | null; // Can be false
  established_date: string | null;
  slider_images?: Array<{
    id: number;
    image: string;
    sequence: number;
  }>;
  tags?: Array<{
    id: number;
    name: string;
  }>;
  initiatives: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  programs: Array<{
    id: number;
    name: string;
    description: string;
  }>;
}

interface OdooTalent {
  id: number;
  name: string;
  category: string | null;
  categories?: string[]; // Alternative field name
  description: string;
  achievements: string;
  image: string | null;
  social_media?: Array<{
    id: number;
    name: string;
    url: string;
  }>;
}

/**
 * Transform Odoo news item to frontend Post type
 * Note: Since Odoo returns translated content based on lang parameter,
 * we need to make 3 API calls (one per language) to get all translations
 */
export function transformOdooNewsToPost(
  odooNews: OdooNewsItem,
  lang: 'ar' | 'en' | 'ku'
): Partial<Post> {
  // Handle slug: can be false, null, or string
  const slug = odooNews.slug
    ? odooNews.slug 
    : odooNews.id.toString();

  // Transform images array: prioritize slider_images, then fallback to images
  const images = odooNews.slider_images?.map((img) => {
    const url = buildImageUrl(img.image) || img.image;
    return url;
  }).filter(Boolean) || 
  odooNews.images?.map((img) => {
    const url = buildImageUrl(img.file_url) || img.file_url;
    return url;
  }).filter(Boolean) || [];

  // Determine featured image: use cover image if available, otherwise use first image from gallery
  // buildImageUrl returns null if path is null/undefined, but we want to preserve the value if it's already a URL
  const coverImage = odooNews.image 
    ? (buildImageUrl(odooNews.image) || odooNews.image) 
    : null;
  const firstGalleryImage = images.length > 0 ? images[0] : null;
  const featuredImage = coverImage || firstGalleryImage || undefined;

  return {
    id: odooNews.id.toString(),
    // For now, we'll use the translated title for all language fields
    // TODO: Make separate API calls for each language to get proper translations
    titleAr: lang === 'ar' ? odooNews.title : odooNews.title,
    titleEn: lang === 'en' ? odooNews.title : odooNews.title,
    titleKu: lang === 'ku' ? odooNews.title : odooNews.title,
    slug,
    bodyRich: {
      ar: lang === 'ar' ? odooNews.content : odooNews.content,
      en: lang === 'en' ? odooNews.content : odooNews.content,
      ku: lang === 'ku' ? odooNews.content : odooNews.content,
    },
    excerpt: {
      ar: lang === 'ar' ? odooNews.summary : odooNews.summary,
      en: lang === 'en' ? odooNews.summary : odooNews.summary,
      ku: lang === 'ku' ? odooNews.summary : odooNews.summary,
    },
    tags: odooNews.tags || [],
    status: 'published', // All fetched news are published
    featuredImage,
    images: images.length > 0 ? images : undefined,
    // Note: department is a string (name), not an ID - we'd need to look it up if we need the ID
    // author is also a string (name), not an ID
    authorId: odooNews.author || 'unknown', // Using author name as fallback
    publishedAt: odooNews.date || new Date().toISOString(),
    createdAt: odooNews.date || new Date().toISOString(),
    updatedAt: odooNews.date || new Date().toISOString(),
  };
}

/**
 * Transform Odoo partner to frontend Partner type
 */
export function transformOdooPartnerToPartner(
  odooPartner: OdooPartner
): Partial<Partner> {
  // Handle code: can be false, null, or string
  const code = odooPartner.code
    ? odooPartner.code
    : null;
  
  // Use slug from backend if available (handle false/null), otherwise generate from code or ID
  const slug = odooPartner.slug
    ? odooPartner.slug 
    : (code?.toLowerCase() || odooPartner.id.toString());
  
  // Transform slider_images to gallery array
  const gallery = odooPartner.slider_images?.map((img) => {
    const url = buildImageUrl(img.image) || img.image;
    return url;
  }).filter(Boolean) || [];
  
  return {
    id: odooPartner.id.toString(),
    slug,
    name: odooPartner.name,
    logo: buildImageUrl(odooPartner.logo) || '/Icons/logo.png',
    summary: odooPartner.title
      ? odooPartner.title 
      : (odooPartner.description ? odooPartner.description.substring(0, 200) : ''),
    description: odooPartner.description || '',
    website: odooPartner.website || undefined,
    email: odooPartner.email || undefined,
    phone: odooPartner.phone || undefined,
    founded: odooPartner.established_date 
      ? new Date(odooPartner.established_date).getFullYear() 
      : undefined,
    categories: odooPartner.partner_type ? [odooPartner.partner_type] : [],
    gallery: gallery.length > 0 ? gallery : undefined,
    initiatives: (odooPartner.initiatives || []).map((init) => ({
      title: init.name,
      description: init.description,
    })),
    programs: (odooPartner.programs || []).map((prog) => ({
      name: prog.name,
      focus: prog.description,
      status: 'ongoing' as const,
    })),
  };
}

/**
 * Transform Odoo talent to frontend Talent type
 */
export function transformOdooTalentToTalent(
  odooTalent: OdooTalent,
  lang: 'ar' | 'en' | 'ku'
): Talent {
  // Map category name to category enum
  const categoryMap: Record<string, Talent['category']> = {
    'Science': 'science',
    'Art': 'art',
    'Sport': 'sport',
    'العلوم': 'science',
    'الفن': 'art',
    'الرياضة': 'sport',
  };

  // Get category from either category or categories array
  const categoryValue = odooTalent.category || (odooTalent.categories && odooTalent.categories[0]) || 'sport';

  // Transform social_media array to frontend format
  const socialMedia: Talent['socialMedia'] = {};
  if (odooTalent.social_media && Array.isArray(odooTalent.social_media)) {
    odooTalent.social_media.forEach((social) => {
      const name = social.name.toLowerCase().trim();
      let url = social.url.trim();
      
      // Ensure URL has protocol
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }
      
      // Map social media names (case-insensitive) to frontend keys
      if (name.includes('facebook') || name === 'fb') {
        socialMedia.facebook = url;
      } else if (name.includes('instagram') || name === 'ig') {
        socialMedia.instagram = url;
      } else if (name.includes('twitter') || name === 'x') {
        socialMedia.twitter = url;
      }
    });
  }

  return {
    id: odooTalent.id.toString(),
    name: odooTalent.name,
    category: categoryMap[categoryValue] || 'sport',
    bioAr: lang === 'ar' ? odooTalent.description : odooTalent.description,
    bioEn: lang === 'en' ? odooTalent.description : odooTalent.description,
    bioKu: lang === 'ku' ? odooTalent.description : odooTalent.description,
    photo: odooTalent.image 
      ? (buildImageUrl(odooTalent.image) || odooTalent.image) 
      : '/Icons/logo.png',
    achievements: {
      ar: lang === 'ar' ? odooTalent.achievements : odooTalent.achievements,
      en: lang === 'en' ? odooTalent.achievements : odooTalent.achievements,
      ku: lang === 'ku' ? odooTalent.achievements : odooTalent.achievements,
    },
    socialMedia: Object.keys(socialMedia).length > 0 ? socialMedia : undefined,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Transform Odoo paginated response to frontend PaginatedResponse
 * Handles both old format (total_records) and new format (pagination object)
 */
export function transformOdooPaginatedResponse<T, U>(
  odooResponse: OdooResponse<T[]> & { pagination?: any },
  transformer: (item: T, lang: 'ar' | 'en' | 'ku') => U,
  page: number,
  pageSize: number,
  lang: 'ar' | 'en' | 'ku' = 'ar'
): PaginatedResponse<U> {
  // Check if response has new pagination structure
  if (odooResponse.pagination) {
    const pagination = odooResponse.pagination;
    return {
      data: odooResponse.data.map((item) => transformer(item, lang)),
      total: pagination.total_records || pagination.totalRecords || odooResponse.data.length,
      page: pagination.current_page || pagination.currentPage || page,
      pageSize: pagination.page_size || pagination.pageSize || pageSize,
      totalPages: pagination.total_pages || pagination.totalPages || Math.ceil((pagination.total_records || odooResponse.data.length) / (pagination.page_size || pageSize)),
    };
  }
  
  // Fallback to old format
  const total = odooResponse.total_records || odooResponse.data.length;
  
  return {
    data: odooResponse.data.map((item) => transformer(item, lang)),
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
  };
}

/**
 * Extract error message from Odoo error response
 */
export function extractErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('data' in error && typeof error.data === 'object' && error.data !== null) {
      if ('message' in error.data && typeof error.data.message === 'string') {
        return error.data.message;
      }
    }
  }
  return 'An unexpected error occurred';
}

/**
 * Utility to convert query params to FormData for POST requests
 */
export function toFormData(params: Record<string, string | number | boolean | undefined>): FormData {
  const formData = new FormData();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(key, String(value));
    }
  });
  return formData;
}

/**
 * Utility to convert query params to URLSearchParams for GET requests
 * (We'll need this when we change Odoo endpoints from POST to GET)
 */
export function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

